import { useEffect, useRef, useState } from 'react';

export function About() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="about" className="relative overflow-hidden py-32 md:py-40">
      {/* Background gradient */}
      <div className="from-alpine-950 via-alpine-900/50 to-alpine-950 absolute inset-0 bg-gradient-to-b" />

      {/* Ambient glow */}
      <div className="bg-forest/[0.05] pointer-events-none absolute top-1/2 left-1/2 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[150px]" />

      <div className="relative z-10 mx-auto max-w-5xl px-6 md:px-8">
        {/* Section header */}
        <div
          className={`mb-20 text-center transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <div className="text-earth-sand/80 font-body mb-6 inline-flex items-center gap-3 text-xs tracking-[0.4em] uppercase">
            <span className="to-earth-sand/50 h-px w-8 bg-gradient-to-r from-transparent" />
            About
            <span className="to-earth-sand/50 h-px w-8 bg-gradient-to-l from-transparent" />
          </div>

          <h2 className="font-display text-earth-cream mb-6 text-4xl tracking-tight md:text-6xl">
            From Vision to Reality
          </h2>

          <p className="font-body mx-auto max-w-2xl text-lg leading-relaxed font-light text-stone-400">
            We approach every project with the same care and precision as crafting a mountain trail
            &mdash; every detail matters, every decision serves the journey.
          </p>

          <p className="font-body mt-6 text-sm text-stone-500">
            Part of the{' '}
            <a
              href="https://corealpine.design"
              target="_blank"
              rel="noopener noreferrer"
              className="text-forest hover:text-forest-light accent-underline transition-colors duration-300"
            >
              Core Alpine
            </a>{' '}
            family
          </p>
        </div>

        {/* Values grid */}
        <div className="grid gap-6 md:grid-cols-3">
          <ValueCard
            title="Precision"
            description="Meticulous attention to detail in every line of code, every design decision, every user interaction."
            icon={<PrecisionIcon />}
            delay={0}
            isVisible={isVisible}
          />
          <ValueCard
            title="Durability"
            description="Solutions built to last, engineered to scale, designed to evolve with your needs."
            icon={<DurabilityIcon />}
            delay={150}
            isVisible={isVisible}
          />
          <ValueCard
            title="Elevation"
            description="Raising the standard for what's possible, pushing boundaries while staying grounded."
            icon={<ElevationIcon />}
            delay={300}
            isVisible={isVisible}
          />
        </div>
      </div>
    </section>
  );
}

interface ValueCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  delay: number;
  isVisible: boolean;
}

function ValueCard({ title, description, icon, delay, isVisible }: ValueCardProps) {
  return (
    <div
      className={`group glass-panel-hover relative rounded-2xl p-8 transition-all duration-700 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}
      style={{ transitionDelay: `${delay + 300}ms` }}
    >
      {/* Top accent line */}
      <div className="group-hover:via-forest/50 absolute top-0 right-8 left-8 h-px bg-gradient-to-r from-transparent via-stone-700/50 to-transparent transition-colors duration-500" />

      {/* Icon */}
      <div className="text-forest group-hover:text-forest-light mb-6 transition-colors duration-300">
        {icon}
      </div>

      {/* Content */}
      <h3 className="font-display text-earth-cream mb-3 text-xl tracking-wide">{title}</h3>
      <p className="font-body text-sm leading-relaxed text-stone-500 transition-colors duration-300 group-hover:text-stone-400">
        {description}
      </p>

      {/* Corner decoration */}
      <div className="group-hover:border-forest/30 absolute right-4 bottom-4 h-8 w-8 border-r border-b border-stone-800/30 transition-colors duration-300" />
    </div>
  );
}

function PrecisionIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="16" cy="16" r="12" />
      <circle cx="16" cy="16" r="6" />
      <circle cx="16" cy="16" r="1.5" fill="currentColor" />
      <path d="M16 4V8M16 24V28M4 16H8M24 16H28" />
    </svg>
  );
}

function DurabilityIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M16 4L4 10V16C4 22.627 9.373 28 16 28C22.627 28 28 22.627 28 16V10L16 4Z" />
      <path d="M12 16L15 19L21 13" />
    </svg>
  );
}

function ElevationIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M4 28L12 16L18 22L28 8" />
      <path d="M22 8H28V14" />
    </svg>
  );
}
